import LanguagesList from 'components/LanguagesList';
import Popover from 'components/Popover';
import { getFilter } from 'data/filters';
import useStores from 'hooks/useStores';
import PopoverFooter from 'modules/common/Popovers/PopoverFooter';
import { PopoverTriggerSource } from 'modules/common/SubBars/ExperienceFilterSubNavbar/usePopoverTrigger';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import React, { useState } from 'react';
import { useTrekker } from 'utils/analytics';

const CSS = require('./LocalSpeaksPopover.module.scss');

interface LocalSpeaksPopoverProps {
  initialSelectedLanguages: number[];
  onClose: ({ source }: { source: PopoverTriggerSource }) => void;
}

const stores = ['FiltersStore', 'SearchStore'];
function getStateFromStore(flux) {
  return {
    aggregations: flux.stores.SearchStore.getAggregations(
      flux.stores.FiltersStore.getFilters(),
    ),
  };
}

const LANGUAGES = getFilter('languages.items[]').subOptions.map(
  ({ name, value, id }) => ({ name, value, id }),
);

function LocalSpeaksPopover({
  initialSelectedLanguages,
  onClose,
}: LocalSpeaksPopoverProps) {
  const trekker = useTrekker();
  const [selectedLanguages, setSelectedLanguages] = useState<number[]>([
    ...initialSelectedLanguages,
  ]);
  const { aggregations } = useStores(stores, getStateFromStore);

  const isPristine = (languages) => {
    if (languages.length !== initialSelectedLanguages.length) {
      return false;
    }
    return languages.every((selectedLanguage) =>
      initialSelectedLanguages.includes(selectedLanguage),
    );
  };

  const onLanguageClick = (value: string) => (event) => {
    const clickedLanguage = parseInt(event.currentTarget.value, 10);
    setSelectedLanguages((currentLanguages) => {
      if (!currentLanguages.includes(clickedLanguage)) {
        return [...currentLanguages, clickedLanguage];
      }
      return currentLanguages.filter((lang) => lang !== clickedLanguage);
    });
  };

  const onApply =
    (source: 'apply button' | 'outside click' | 'escape button') => (e) => {
      e?.stopPropagation();

      if (isPristine(selectedLanguages)) {
        onClose({ source });
        return;
      }

      const langFilter = getFilter('languages.items[]');
      langFilter.subOptions = langFilter.subOptions.filter((subOption) =>
        selectedLanguages.includes(subOption.id),
      );

      routeToSearchURL({ filtersToAdd: [langFilter] });

      trekker.event('click', {
        action: 'languages filter',
        buttonText: 'apply',
        languages: LANGUAGES.filter((lang) =>
          selectedLanguages.includes(lang.id),
        )
          .map((lang) => lang.name)
          .join(', '),
      });

      onClose({ source });
    };

  const onClear = (source) => {
    trekker.event('click', {
      action: 'languages filter',
      buttonText: 'clear',
    });
    const langFilter = getFilter('languages.items[]');
    langFilter.subOptions = [];

    routeToSearchURL({ filtersToAdd: [langFilter] });
    onClose({ source: 'clear button' });
  };

  return (
    <Popover
      show
      underNavbar
      name="localSpeaksPopover"
      className={CSS.Popover}
      onClose={onApply('escape button')}
      onOutSideClick={onApply('outside click')}
    >
      <LanguagesList
        selectedLanguages={selectedLanguages}
        onLanguageClick={onLanguageClick}
        aggregations={aggregations}
      />
      <PopoverFooter onClear={onClear} onApply={onApply('apply button')} />
    </Popover>
  );
}

export default LocalSpeaksPopover;
